import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Flag, EyeOff, Trash2, CheckCircle2, ChevronDown, ExternalLink, Loader2 } from "lucide-react";
import { API } from "../App";
import { Button } from "./ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

const ROUTES = {
  social_post: "/community", community_event: "/community", meetup: "/meetups",
  wage_entry: "/wages", poll: "/polls", poll_suggestion: "/polls",
};
const fmt = (iso) => new Date(iso).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

const ReportGroup = ({ g, resolved, onDismiss, onDelete }) => {
  const [open, setOpen] = useState(false);
  const key = `${g.content_type}-${g.content_id}`;
  return (
    <div className="bg-white border border-[#E5DFEC] rounded-2xl p-4" data-testid={`report-group-${key}`}>
      <div className="flex flex-col md:flex-row md:items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1.5">
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#F0ECF7] text-[#6B4E8A]">{g.type_label}</span>
            <span className="text-xs font-semibold text-[#E07A5F] inline-flex items-center gap-1" data-testid={`report-count-${key}`}>
              <Flag className="w-3 h-3" />{g.report_count} report{g.report_count === 1 ? "" : "s"}
            </span>
            {g.hidden && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FEE2E2] text-[#B91C1C] inline-flex items-center gap-1" data-testid={`report-hidden-${key}`}><EyeOff className="w-3 h-3" />Auto-hidden</span>}
            {!g.content_exists && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#F1F5F9] text-[#64748B]">Content removed</span>}
            {g.reason_labels.map((r) => <span key={r} className="text-[11px] px-2 py-0.5 rounded-full border border-[#E5DFEC] text-[#475569]">{r}</span>)}
          </div>
          <p className="text-sm text-[#334155] whitespace-pre-wrap break-words line-clamp-3">{g.content_preview || "—"}</p>
          <p className="text-xs text-[#94A3B8] mt-1">{g.author_name ? `by ${g.author_name} · ` : ""}latest report {fmt(g.latest_at)}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <a href={ROUTES[g.content_type]} className="text-xs text-[#6B4E8A] inline-flex items-center gap-1 hover:underline" data-testid={`report-view-${key}`}><ExternalLink className="w-3 h-3" />View section</a>
          {!resolved && (
            <>
              <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => onDismiss(g)} data-testid={`report-dismiss-${key}`}>
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Dismiss{g.hidden ? " & restore" : ""}
              </Button>
              {g.content_exists && (
                <Button size="sm" className="rounded-full text-xs bg-[#EF4444] hover:bg-[#DC2626] text-white" onClick={() => onDelete(g)} data-testid={`report-delete-${key}`}>
                  <Trash2 className="w-3.5 h-3.5 mr-1" />Delete content
                </Button>
              )}
            </>
          )}
        </div>
      </div>
      <button type="button" onClick={() => setOpen(!open)} className="mt-3 text-xs text-[#64748B] inline-flex items-center gap-1 hover:text-[#6B4E8A]" data-testid={`report-toggle-${key}`}>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />{open ? "Hide" : "Show"} reporters
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          {g.reports.map((r) => (
            <div key={r.report_id} className="bg-[#FAF7F2] rounded-xl px-3 py-2 text-xs text-[#475569]" data-testid={`report-row-${r.report_id}`}>
              <span className="font-medium text-[#6B4E8A]">{r.reporter_name}</span> <span className="text-[#94A3B8]">({r.reporter_email})</span> · {r.reason.replace("_", " ")} · {fmt(r.created_at)}
              {r.note && <p className="mt-1 italic">“{r.note}”</p>}
              {r.resolved_by_name && <p className="mt-1 text-[#94A3B8]">Resolved by {r.resolved_by_name} · {fmt(r.resolved_at)}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const ReportsQueuePanel = ({ onChange }) => {
  const [status, setStatus] = useState("open");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingDelete, setPendingDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems((await axios.get(`${API}/admin/reports?status=${status}`)).data); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't load reports"); }
    finally { setLoading(false); }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const act = async (g, action, msg) => {
    try {
      await axios.post(`${API}/admin/reports/${g.content_type}/${g.content_id}/${action}`);
      toast.success(msg);
      setItems((prev) => prev.filter((x) => !(x.content_type === g.content_type && x.content_id === g.content_id)));
      onChange && onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  return (
    <div className="space-y-4" data-testid="reports-queue-panel">
      <div className="flex items-center gap-2">
        {["open", "resolved"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={`px-4 py-1.5 text-sm rounded-full border transition-colors capitalize ${status === s ? "bg-[#6B4E8A] text-white border-[#6B4E8A]" : "border-[#E5DFEC] text-[#64748B] hover:border-[#6B4E8A]"}`} data-testid={`reports-filter-${s}`}>{s}</button>
        ))}
      </div>
      {loading ? (
        <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[#6B4E8A]" /></div>
      ) : items.length === 0 ? (
        <div className="py-12 text-center bg-white border border-dashed border-[#E5DFEC] rounded-2xl text-sm text-[#64748B]" data-testid="reports-empty">
          <Flag className="w-6 h-6 mx-auto mb-2 text-[#CBD5E1]" />{status === "open" ? "No open reports — the community is behaving!" : "No resolved reports yet."}
        </div>
      ) : (
        items.map((g) => (
          <ReportGroup key={`${g.content_type}-${g.content_id}`} g={g} resolved={status !== "open"}
            onDismiss={(x) => act(x, "dismiss", "Reports dismissed")} onDelete={setPendingDelete} />
        ))
      )}
      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent data-testid="report-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this {pendingDelete?.type_label.toLowerCase()}?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the content and anything attached to it (comments, responses, upvotes). All reports on it will be marked resolved.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction className="rounded-full bg-[#EF4444] hover:bg-[#DC2626] text-white" data-testid="report-delete-confirm"
              onClick={() => { act(pendingDelete, "delete-content", "Content deleted"); setPendingDelete(null); }}>
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ReportsQueuePanel;
